<template>
    <div class="student-portal-forum" :class="{ dark }">
        <div class="student-portal-forum__inner">
            <header class="student-portal-forum__header">
                <div class="student-portal-forum__header-main">
                    <h1 class="card-title">Fóruns</h1>
                    <p class="card-subtitle">
                        Acompanhe as discussões e participe dos tópicos da turma
                    </p>
                </div>
                <v-btn
                    color="#1ac4cd"
                    variant="elevated"
                    prepend-icon="mdi-refresh"
                    class="student-portal-forum__refresh-btn"
                    :loading="loading"
                    @click="fetchForums"
                >
                    Atualizar
                </v-btn>
            </header>

            <v-card
                v-if="loading && !hasReady"
                class="portal-forum__card portal-forum__card--surface pa-8"
                elevation="0"
            >
                <v-row justify="center">
                    <v-col cols="auto">
                        <v-progress-circular
                            indeterminate
                            color="primary"
                            size="64"
                        ></v-progress-circular>
                        <p class="text-center mt-4 student-portal-forum__loading-text">
                            Carregando fóruns...
                        </p>
                    </v-col>
                </v-row>
            </v-card>

            <v-card
                v-else
                elevation="0"
                class="portal-forum__card portal-forum__card--surface p-2 p-md-4"
            >
                <div class="portal-forum__filters mb-4">
                    <v-text-field
                        v-model.trim="filters.query"
                        class="portal-forum__filter-search"
                        label="Buscar por título ou descrição"
                        variant="outlined"
                        density="comfortable"
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        hide-details
                    />
                    <v-select
                        v-model="filters.author"
                        class="portal-forum__filter-author"
                        :items="authorFilterOptions"
                        item-title="label"
                        item-value="value"
                        label="Autor"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                    />
                    <v-select
                        v-model="filters.period"
                        class="portal-forum__filter-period"
                        :items="periodFilterOptions"
                        item-title="label"
                        item-value="value"
                        label="Período"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                    />
                    <v-btn
                        variant="tonal"
                        color="grey-darken-1"
                        prepend-icon="mdi-filter-off-outline"
                        :disabled="!hasActiveFilters"
                        @click="clearFilters"
                    >
                        Limpar
                    </v-btn>
                </div>

                <v-data-table
                    :headers="headers"
                    :items="filteredForums"
                    :items-per-page="itemsPerPage"
                    :hide-default-footer="shouldHidePagination"
                    class="forum-table elevation-0"
                    no-data-text="Nenhum fórum encontrado"
                >
                    <template v-slot:[`item.title`]="{ item }">
                        <div class="font-weight-medium text-body-1 text-left py-2">
                            {{ item.title }}
                        </div>
                    </template>

                    <template v-slot:[`item.description`]="{ item }">
                        <div class="forum-table__description">
                            {{ truncateText(item.description, 90) }}
                        </div>
                    </template>

                    <template v-slot:[`item.dateInsert`]="{ item }">
                        <div class="font-weight-medium d-none d-md-block">
                            {{ formatDate(item.dateInsert) }}
                        </div>
                    </template>

                    <template v-slot:[`item.commentsQty`]="{ item }">
                        <v-chip
                            color="info"
                            size="small"
                            variant="tonal"
                        >
                            {{ Number(item.commentsQty || 0) }}
                            {{ Number(item.commentsQty || 0) === 1 ? 'resposta' : 'respostas' }}
                        </v-chip>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                        <div class="d-flex gap-2 justify-center">
                            <v-btn
                                color="#1ac4cd"
                                variant="text"
                                icon="mdi-eye"
                                size="small"
                                @click="goToForum(item)"
                            ></v-btn>
                        </div>
                    </template>

                    <template v-slot:no-data>
                        <div class="text-center pa-8">
                            <v-icon
                                size="80"
                                color="grey-lighten-1"
                                class="mb-4"
                            >
                                mdi-forum-outline
                            </v-icon>
                            <h3 class="text-h5 mb-2">
                                Nenhum fórum encontrado
                            </h3>
                            <p class="text-body-1 text-medium-emphasis">
                                Ajuste os filtros para localizar discussões
                            </p>
                        </div>
                    </template>
                </v-data-table>
            </v-card>
        </div>
    </div>
</template>

<script>
import api from '@/utils/portal/forum.js';
import { mapState } from 'vuex';
import { toast } from 'vue3-toastify';

export default {
    data() {
        return {
            loading: false,
            hasReady: false,
            forums: [],
            itemsPerPage: 10,
            filters: {
                query: '',
                author: 'all',
                period: 'all',
            },
            periodFilterOptions: [
                { label: 'Todo período', value: 'all' },
                { label: 'Últimos 7 dias', value: 7 },
                { label: 'Últimos 30 dias', value: 30 },
                { label: 'Últimos 90 dias', value: 90 },
            ],
            headers: [
                {
                    title: 'Título',
                    key: 'title',
                    sortable: true,
                    width: '26%',
                    minWidth: 200,
                    align: 'start',
                },
                {
                    title: 'Descrição',
                    key: 'description',
                    sortable: false,
                    width: '26%',
                    align: 'start',
                },
                {
                    title: 'Data de Criação',
                    key: 'dateInsert',
                    sortable: true,
                    width: '16%',
                    align: 'center',
                    class: 'd-none d-md-table-cell',
                },
                {
                    title: 'Autor',
                    key: 'userName',
                    sortable: true,
                    width: '16%',
                    align: 'center',
                },
                {
                    title: 'Respostas',
                    key: 'commentsQty',
                    sortable: true,
                    width: '10%',
                    align: 'center',
                },
                {
                    title: 'Ações',
                    key: 'actions',
                    sortable: false,
                    width: '6%',
                    align: 'center',
                },
            ],
        };
    },
    computed: {
        ...mapState(['token', 'dark']),
        shouldHidePagination() {
            return this.filteredForums.length <= this.itemsPerPage;
        },
        hasActiveFilters() {
            return (
                this.filters.query !== '' ||
                this.filters.author !== 'all' ||
                this.filters.period !== 'all'
            );
        },
        authorFilterOptions() {
            const names = Array.from(
                new Set(
                    this.forums
                        .map((item) => String(item.userName || '').trim())
                        .filter((name) => name.length > 0)
                )
            ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

            return [
                { label: 'Todos os autores', value: 'all' },
                ...names.map((name) => ({ label: name, value: name })),
            ];
        },
        filteredForums() {
            const q = this.filters.query.toLowerCase();
            const author = this.filters.author;
            const period = this.filters.period;
            const now = new Date();

            return this.forums.filter((item) => {
                const byQuery =
                    !q ||
                    String(item.title || '').toLowerCase().includes(q) ||
                    String(item.description || '').toLowerCase().includes(q);

                const byAuthor =
                    author === 'all' || String(item.userName || '').trim() === author;

                let byPeriod = true;
                if (period !== 'all' && item.dateInsert) {
                    const dt = new Date(item.dateInsert);
                    if (Number.isNaN(dt.getTime())) {
                        byPeriod = false;
                    } else {
                        const diffMs = now.getTime() - dt.getTime();
                        const diffDays = diffMs / (1000 * 60 * 60 * 24);
                        byPeriod = diffDays <= Number(period);
                    }
                }

                return byQuery && byAuthor && byPeriod;
            });
        },
    },
    methods: {
        formatDate(date) {
            if (!date) return '-';
            const d = new Date(date);
            return d.toLocaleDateString('pt-BR');
        },
        truncateText(text, maxLength = 80) {
            const value = String(text || '');
            if (value.length <= maxLength) return value;
            return `${value.slice(0, maxLength)}...`;
        },
        async fetchForums() {
            this.loading = true;
            try {
                const response = await api.getDataForum(this.token);
                this.forums = Array.isArray(response) ? response : [];
            } catch (e) {
                toast.error('Erro ao carregar fóruns');
                console.error('Erro ao carregar fóruns', e);
            } finally {
                this.loading = false;
                this.hasReady = true;
            }
        },
        goToForum(forum) {
            this.$router.push(`/aluno/forum/${forum.id}`);
        },
        clearFilters() {
            this.filters.query = '';
            this.filters.author = 'all';
            this.filters.period = 'all';
        },
    },
    mounted() {
        this.fetchForums();
    },
};
</script>

<style scoped>
.student-portal-forum {
    --dash-bg: #eef0f4;
    --dash-bg-elevated: #ffffff;
    --dash-text: #111827;
    --dash-text-muted: #6b7280;
    --dash-border: rgba(15, 23, 42, 0.08);
    min-height: 100%;
    background-color: var(--dash-bg);
    box-sizing: border-box;
}

.student-portal-forum.dark {
    --dash-bg: #0f0e12;
    --dash-bg-elevated: #1a191f;
    --dash-text: #f3f4f6;
    --dash-text-muted: #9ca3af;
    --dash-border: rgba(255, 255, 255, 0.08);
}

.student-portal-forum__inner {
    max-width: 1920px;
    margin: 0 auto;
    padding: 18px 14px 32px;
    box-sizing: border-box;
}

.student-portal-forum__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    text-align: left;
    margin-bottom: 28px;
}

.student-portal-forum__header-main {
    min-width: 0;
}

.student-portal-forum__refresh-btn {
    flex-shrink: 0;
    margin-top: 4px;
}

.student-portal-forum__loading-text {
    color: var(--dash-text-muted);
}

.portal-forum__card--surface {
    background-color: var(--dash-bg-elevated) !important;
    color: var(--dash-text);
    border: 1px solid #e8eaf0;
    border-radius: 12px;
    box-shadow:
        0 1px 3px rgba(37, 8, 145, 0.06),
        0 6px 22px rgba(15, 23, 42, 0.06);
}

.portal-forum__card {
    padding: 14px !important;
}

:deep(.forum-table) {
    border: 1px solid #e8eaf0;
    border-radius: 10px;
    overflow: hidden;
}

:deep(.forum-table .v-data-table__th) {
    background: linear-gradient(180deg, #fafbff 0%, #ffffff 100%);
    color: #374151;
    font-weight: 600;
    border-bottom: 1px solid #eef0f4;
}

:deep(.forum-table .v-data-table__tr:hover) {
    background: rgba(37, 8, 145, 0.03);
}

:deep(.forum-table .v-data-table__td) {
    border-bottom: 1px solid #f1f3f7;
}

.forum-table__description {
    color: var(--dash-text-muted);
    line-height: 1.35;
}

.portal-forum__filters {
    display: grid;
    grid-template-columns: minmax(260px, 1.6fr) minmax(200px, 1fr) minmax(180px, 0.9fr) auto;
    gap: 12px;
    align-items: center;
    border: 1px solid #e8eaf0;
    border-radius: 10px;
    padding: 10px;
    background: #fafbfc;
}

    @media (max-width: 960px) {
    .student-portal-forum__header {
        margin-bottom: 20px;
        flex-direction: column;
        align-items: stretch;
    }

    .student-portal-forum__refresh-btn {
        align-self: flex-start;
        margin-top: 0;
    }

    .portal-forum__filters {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 959px) {
    :deep(.forum-table th:nth-child(3)),
    :deep(.forum-table td:nth-child(3)) {
        display: none !important;
    }
}

.dark.student-portal-forum :deep(.forum-table) {
    background-color: var(--dash-bg-elevated);
    color: var(--dash-text);
    border-color: #3d414d;
}

.dark.student-portal-forum :deep(.forum-table .v-data-table__th) {
    background-color: #252730;
    color: var(--dash-text-muted);
    border-bottom-color: #3d414d;
}

.dark.student-portal-forum :deep(.forum-table .v-data-table__td) {
    color: var(--dash-text);
    border-bottom-color: #2d313c;
}

.dark.student-portal-forum :deep(.forum-table .v-data-table__tr:hover) {
    background: rgba(109, 90, 205, 0.12);
}

.dark.student-portal-forum :deep(.v-data-table-footer) {
    background-color: var(--dash-bg-elevated);
    color: var(--dash-text-muted);
    border-color: var(--dash-border);
}

.dark.student-portal-forum .portal-forum__card--surface {
    border-color: #353945;
    box-shadow:
        0 1px 4px rgba(0, 0, 0, 0.35),
        0 8px 22px rgba(0, 0, 0, 0.24);
}

.dark.student-portal-forum .portal-forum__filters {
    background: #1e2028;
    border-color: #3d414d;
}

.card-title {
    font-size: 28px;
    font-weight: 700;
    color: var(--dash-text);
    margin-bottom: 8px;
}

.card-subtitle {
    font-size: 16px;
    color: var(--dash-text-muted);
    margin-bottom: 0;
}
</style>